import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Smartphone, Zap, ArrowRight } from "lucide-react";

const STORAGE_KEY = "fitsmith_pwa_banner_last_shown";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function shouldShowBanner() {
  try {
    // Niet tonen als al geïnstalleerd als PWA
    if (window.matchMedia("(display-mode: standalone)").matches) return false;
    if (window.navigator.standalone === true) return false;
    // Max 1x per dag
    const last = localStorage.getItem(STORAGE_KEY);
    if (!last) return true;
    return Date.now() - parseInt(last, 10) > ONE_DAY_MS;
  } catch {
    return false;
  }
}

export default function PwaInstallBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    // Vertraging zodat gebruikers eerst met de pagina kunnen interacteren
    const timer = setTimeout(() => {
      if (shouldShowBanner()) {
        setVisible(true);
        setTimeout(() => setAnimateIn(true), 50);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setAnimateIn(false);
    setTimeout(() => setVisible(false), 350);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  };

  const handleView = () => {
    handleClose();
    navigate("/app-installeren");
  };

  if (!visible) return null;

  return (
    <>
      {/* Banner — slide omhoog vanaf onderkant (geen backdrop zodat pagina-interacties niet geblokkeerd worden) */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[40] transition-transform duration-350 ease-out"
        style={{ transform: animateIn ? "translateY(0)" : "translateY(100%)" }}
      >
        <div className="bg-[#0f0f0f] border-t border-white/10 rounded-t-3xl px-5 pt-6 pb-8 max-w-lg mx-auto shadow-2xl">
          {/* Trekker */}
          <div className="w-10 h-1 bg-white/15 rounded-full mx-auto mb-5" />

          <div className="flex items-start gap-4">
            {/* App icon */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg, #C9A84C22, #C9A84C10)", border: "1.5px solid #C9A84C30" }}>
              <Smartphone size={24} style={{ color: "#C9A84C" }} />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-white font-black text-base leading-tight mb-1">
                Gebruik FitSmith als app
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Sneller toegang tot je schema's, direct vanaf je beginscherm — zonder app store.
              </p>
            </div>

            <button
              onClick={handleClose}
              data-testid="pwa-banner-close"
              className="w-8 h-8 rounded-full bg-white/6 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/10 transition-all shrink-0 mt-0.5"
            >
              <X size={14} />
            </button>
          </div>

          {/* Voordelen */}
          <div className="mt-4 mb-5 flex gap-3">
            {[
              { icon: <Zap size={12} />, label: "Direct starten" },
              { icon: <Smartphone size={12} />, label: "Geen app store nodig" },
              { icon: <ArrowRight size={12} />, label: "Gratis" },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 bg-white/4 border border-white/6 rounded-full px-3 py-1.5">
                <span style={{ color: "#C9A84C" }}>{icon}</span>
                <span className="text-zinc-400 text-xs font-medium">{label}</span>
              </div>
            ))}
          </div>

          {/* Knoppen */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              data-testid="pwa-banner-dismiss"
              className="flex-1 py-3 rounded-2xl border border-white/10 text-zinc-400 text-sm font-semibold hover:bg-white/5 transition-colors"
            >
              Sluiten
            </button>
            <button
              onClick={handleView}
              data-testid="pwa-banner-view"
              className="flex-1 py-3 rounded-2xl text-black text-sm font-black flex items-center justify-center gap-2 transition-all active:scale-95"
              style={{ background: "#C9A84C" }}
            >
              Bekijk uitleg <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
